import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ClockIcon, 
  UserIcon,
  UserGroupIcon
} from '../../../../components/ui/Icons';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent, 
  Badge 
} from '../../../../components/ui';
import dayjs from 'dayjs';
import ApprovalProgressOverview from './ApprovalProgressOverview';

const ApprovalSteps = ({ selectedRecord }) => {
  const { theme } = useTheme();

  // Helper function to get approver display name
  const getApproverDisplayName = (step) => {
    if (step.assignedApprover) {
      return step.assignedApprover.userDisplayName || 
             (step.assignedApprover.userFirstName && step.assignedApprover.userLastName ? 
              `${step.assignedApprover.userFirstName} ${step.assignedApprover.userLastName}` : 
              step.assignedApprover.userEmail) || 'Unknown User';
    }
    return null;
  };

  // Helper function to get approver email
  const getApproverEmail = (step) => {
    if (step.assignedApprover) {
      return step.assignedApprover.userEmail;
    }
    return null;
  };

  // Helper function to get step status based on actual step data
  const getStepStatus = (step) => {
    if (step.rejectedAt || step.rejectedBy) {
      return 'rejected';
    }
    if (step.stepType === 'review') {
      return step.reviewedAt || step.reviewedBy ? 'reviewed' : 'pending';
    }
    return step.approvedAt || step.approvedBy ? 'approved' : 'pending';
  };

  // Helper function to get step status name for display
  const getStepStatusName = (step) => {
    if (step.rejectedAt || step.rejectedBy) {
      return step.status?.name || 'Rejected';
    }
    if (step.stepType === 'review') {
      return step.reviewedAt || step.reviewedBy ? (step.status?.name || 'Reviewed') : (step.status?.name || 'Pending');
    }
    return step.approvedAt || step.approvedBy ? (step.status?.name || 'Approved') : (step.status?.name || 'Pending');
  };

  if (!selectedRecord.steps || selectedRecord.steps.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center">
            <UserGroupIcon className="w-4 h-4 mr-2" />
            Approval Steps
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className={cn("w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3", theme.colors.surface)}>
              <CheckCircleIcon className={cn("w-6 h-6", theme.text.secondary)} />
            </div>
            <h3 className={cn("text-sm font-medium mb-1", theme.text.primary)}>No Steps Defined</h3>
            <p className={cn("text-xs", theme.text.secondary)}>
              This approval request doesn't have any defined steps yet.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Progress Overview */}
      <ApprovalProgressOverview selectedRecord={selectedRecord} />

      {/* Detailed Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center">
            <CheckCircleIcon className="w-4 h-4 mr-2" />
            Approval Steps Details
          </CardTitle>
          <CardDescription>
            Detailed information about each approval step
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {selectedRecord.steps.map((step, index) => (
              <div key={step.id || index} className={cn("p-4 rounded-lg border", theme.border.light, theme.colors.background)}>
                {/* Step Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    {(() => {
                      const stepStatus = getStepStatus(step);
                      return (
                        <div className={cn("w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold", 
                          stepStatus === 'approved' || stepStatus === 'reviewed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                          stepStatus === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                          stepStatus === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                          'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        )}>
                          {stepStatus === 'approved' || stepStatus === 'reviewed' ? (
                            <CheckCircleIcon className="w-4 h-4" />
                          ) : stepStatus === 'pending' ? (
                            <ClockIcon className="w-4 h-4" />
                          ) : stepStatus === 'rejected' ? (
                            <XCircleIcon className="w-4 h-4" />
                          ) : (
                            <span>{step.stepNumber || index + 1}</span>
                          )}
                        </div>
                      );
                    })()}
                    <div>
                      <h4 className={cn("text-sm font-semibold", theme.text.primary)}>
                        {step.stepName || `Step ${step.stepNumber || index + 1}`}
                        {step.stepType && (
                          <Badge variant="outline" size="sm" className="ml-2">
                            {step.stepType === 'review' ? 'Review' : 'Approval'}
                          </Badge>
                        )}
                      </h4>
                      <p className={cn("text-xs", theme.text.secondary)}>
                        Step {step.stepNumber || index + 1} of {selectedRecord.steps.length}
                      </p>
                    </div>
                  </div>
                  {(() => {
                    const stepStatus = getStepStatus(step);
                    return (
                      <Badge 
                        variant={stepStatus === 'approved' || stepStatus === 'reviewed' ? 'success' : 
                               stepStatus === 'pending' ? 'warning' : 
                               stepStatus === 'rejected' ? 'destructive' : 'secondary'}
                        size="sm"
                      >
                        {getStepStatusName(step)}
                      </Badge>
                    );
                  })()}
                </div>

                {/* Approver Information */}
                <div className="space-y-3">
                    <div className={cn("p-3 rounded-lg border", theme.border.light, theme.colors.surface)}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <UserIcon className={cn("w-4 h-4", theme.text.primary)} />
                        <span className={cn("text-sm font-semibold", theme.text.primary)}>
                          {step.stepType === 'review' ? 'Reviewer' : 'Approver'}
                        </span>
                      </div>
                      {(() => {
                        const stepStatus = getStepStatus(step);
                        return (
                          <Badge 
                            variant={stepStatus === 'approved' || stepStatus === 'reviewed' ? 'success' : 
                                   stepStatus === 'pending' ? 'warning' : 
                                   stepStatus === 'rejected' ? 'destructive' : 'secondary'}
                            size="sm"
                          >
                            {getStepStatusName(step)}
                          </Badge>
                        );
                      })()}
                    </div>
                    {(() => {
                      const approverDisplayName = getApproverDisplayName(step);
                      const approverEmail = getApproverEmail(step);
                      
                      if (approverDisplayName) {
                        return (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className={cn("text-sm font-medium", theme.text.secondary)}>Name:</span>
                              <span className={cn("text-sm font-semibold", theme.text.primary)}>
                                {approverDisplayName}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className={cn("text-xs", theme.text.muted)}>Type:</span>
                              <Badge variant="outline" size="sm">
                                {step.approverType?.charAt(0).toUpperCase() + step.approverType?.slice(1)}
                              </Badge>
                            </div>
                          </div>
                        );
                      } else if (step.approverId && step.approverType) {
                        return (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className={cn("text-sm font-medium", theme.text.secondary)}>Approver Type:</span>
                              <Badge variant="outline" size="sm">
                                {step.approverType?.charAt(0).toUpperCase() + step.approverType?.slice(1)}
                              </Badge>
                            </div>
                            {step.approverType !== 'user' && (
                              <div className={cn("text-xs italic", theme.text.muted)}>
                                {step.approverType === 'role' ? 'Role-based approver assignment' : 
                                 step.approverType === 'department' ? 'Department-based approver assignment' : 
                                 'Approver to be resolved'}
                              </div>
                            )}
                            {step.approverType === 'user' && step.approverId && !step.assignedApprover && (
                              <div className={cn("text-xs italic", theme.text.muted)}>
                                User information not available (check backend to ensure assignedApprover relation is included)
                              </div>
                            )}
                          </div>
                        );
                      } else {
                        return (
                          <div className={cn("text-sm", theme.text.muted)}>
                            No approver assigned
                          </div>
                        );
                      }
                    })()}
                  </div>

                  {/* Approval Action Details */}
                  {step.approvedBy && (
                    <div className={cn("p-3 rounded-lg border", theme.border.light, theme.colors.surface)}>
                      <div className="flex items-center space-x-2 mb-2">
                        <CheckCircleIcon className={cn("w-4 h-4", theme.text.success)} />
                        <span className={cn("text-sm font-semibold", theme.text.primary)}>Approved</span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className={cn("text-sm font-medium", theme.text.secondary)}>Approved By:</span>
                          <span className={cn("text-sm font-semibold", theme.text.primary)}>
                            {step.approvedBy.userDisplayName || 
                             (step.approvedBy.userFirstName && step.approvedBy.userLastName ? 
                              `${step.approvedBy.userFirstName} ${step.approvedBy.userLastName}` : 
                              step.approvedBy.userEmail) || 'Unknown User'}
                          </span>
                        </div>
                        {step.approvedAt && (
                          <div className="flex items-center justify-between">
                            <span className={cn("text-xs", theme.text.muted)}>Approved At:</span>
                            <span className={cn("text-xs font-mono", theme.text.primary)}>
                              {dayjs(step.approvedAt).format('MMM DD, YYYY HH:mm')}
                            </span>
                          </div>
                        )}
                        {step.approvalComments && (
                          <div className="mt-2">
                            <span className={cn("text-xs font-medium", theme.text.secondary)}>Comments:</span>
                            <p className={cn("text-xs mt-1", theme.text.primary)}>{step.approvalComments}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Review Action Details */}
                  {step.reviewedBy && (
                    <div className={cn("p-3 rounded-lg border", theme.border.light, theme.colors.surface)}>
                      <div className="flex items-center space-x-2 mb-2">
                        <CheckCircleIcon className={cn("w-4 h-4", theme.text.info)} />
                        <span className={cn("text-sm font-semibold", theme.text.primary)}>Reviewed</span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className={cn("text-sm font-medium", theme.text.secondary)}>Reviewed By:</span>
                          <span className={cn("text-sm font-semibold", theme.text.primary)}>
                            {step.reviewedBy.userDisplayName || 
                             (step.reviewedBy.userFirstName && step.reviewedBy.userLastName ? 
                              `${step.reviewedBy.userFirstName} ${step.reviewedBy.userLastName}` : 
                              step.reviewedBy.userEmail) || 'Unknown User'}
                          </span>
                        </div>
                        {step.reviewedAt && (
                          <div className="flex items-center justify-between">
                            <span className={cn("text-xs", theme.text.muted)}>Reviewed At:</span>
                            <span className={cn("text-xs font-mono", theme.text.primary)}>
                              {dayjs(step.reviewedAt).format('MMM DD, YYYY HH:mm')}
                            </span>
                          </div>
                        )}
                        {step.reviewComments && (
                          <div className="mt-2">
                            <span className={cn("text-xs font-medium", theme.text.secondary)}>Review Comments:</span>
                            <p className={cn("text-xs mt-1", theme.text.primary)}>{step.reviewComments}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Rejection Action Details */}
                  {step.rejectedBy && (
                    <div className={cn("p-3 rounded-lg border border-red-200", theme.colors.surface)}>
                      <div className="flex items-center space-x-2 mb-2">
                        <XCircleIcon className={cn("w-4 h-4", theme.text.destructive)} />
                        <span className={cn("text-sm font-semibold", theme.text.primary)}>Rejected</span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className={cn("text-sm font-medium", theme.text.secondary)}>Rejected By:</span>
                          <span className={cn("text-sm font-semibold", theme.text.primary)}>
                            {step.rejectedBy.userDisplayName || 
                             (step.rejectedBy.userFirstName && step.rejectedBy.userLastName ? 
                              `${step.rejectedBy.userFirstName} ${step.rejectedBy.userLastName}` : 
                              step.rejectedBy.userEmail) || 'Unknown User'}
                          </span>
                        </div>
                        {step.rejectedAt && (
                          <div className="flex items-center justify-between">
                            <span className={cn("text-xs", theme.text.muted)}>Rejected At:</span>
                            <span className={cn("text-xs font-mono", theme.text.primary)}>
                              {dayjs(step.rejectedAt).format('MMM DD, YYYY HH:mm')}
                            </span>
                          </div>
                        )}
                        {step.rejectionReason && (
                          <div className="mt-2">
                            <span className={cn("text-xs font-medium", theme.text.secondary)}>Rejection Reason:</span>
                            <p className={cn("text-xs mt-1", theme.text.primary)}>{step.rejectionReason}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step Configuration */}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className={cn("font-medium", theme.text.secondary)}>Step:</span>
                      <span className={cn("font-medium", theme.text.primary)}>{step.stepNumber || index + 1}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={cn("font-medium", theme.text.secondary)}>Required:</span>
                      <Badge variant={step.isRequired ? 'success' : 'secondary'} size="sm">
                        {step.isRequired ? 'Yes' : 'No'}
                      </Badge>
                    </div>
                  </div>

                  {/* Step Type Badge */}
                  {step.stepType && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <span className={cn("text-xs font-medium", theme.text.secondary)}>Step Type:</span>
                        <Badge variant="outline" size="sm">
                          {step.stepType === 'review' ? 'Review' : 'Approval'}
                        </Badge>
                      </div>
                    </div>
                  )}

                  {/* Timeline */}
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between text-xs">
                      <span className={cn("font-medium", theme.text.secondary)}>Created:</span>
                      <span className={cn("font-mono", theme.text.primary)}>
                        {dayjs(step.createdAt).format('MMM DD, YYYY HH:mm')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs mt-1">
                      <span className={cn("font-medium", theme.text.secondary)}>Updated:</span>
                      <span className={cn("font-mono", theme.text.primary)}>
                        {dayjs(step.updatedAt).format('MMM DD, YYYY HH:mm')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default ApprovalSteps;
